import { assertIsDefined, isDefined, isNotDefined } from '@sgme/fp';

import type { CheckedSwap } from '@/store/swap/api/checkSwapByTradeCapture.ts';
import type { FxSwap, FxSwapNds, FxSwapRegular } from '@/store/swap/state.ts';
import type { TradeCapture, TradeCaptureErrors, TradeCaptureWarnings } from '@/store/types.ts';
import type { TradeCaptureResponseErrors, TradeCaptureResponseWarnings } from '@/types/trade-capture.ts';
import { isIsoDate } from '@/utils/date.ts';
import {
  getErrorPreviousNumberValue,
  getErrorPreviousStringDate,
  getNotional,
  getNotionalCurrencyIndex,
} from '@/utils/tradeCapture.ts';

// ███████╗██████╗  ██████╗ ███╗   ███╗    ████████╗██████╗  █████╗ ██████╗ ███████╗     ██████╗ █████╗ ██████╗ ████████╗██╗   ██╗██████╗ ███████╗
// ██╔════╝██╔══██╗██╔═══██╗████╗ ████║    ╚══██╔══╝██╔══██╗██╔══██╗██╔══██╗██╔════╝    ██╔════╝██╔══██╗██╔══██╗╚══██╔══╝██║   ██║██╔══██╗██╔════╝
// █████╗  ██████╔╝██║   ██║██╔████╔██║       ██║   ██████╔╝███████║██║  ██║█████╗      ██║     ███████║██████╔╝   ██║   ██║   ██║██████╔╝█████╗
// ██╔══╝  ██╔══██╗██║   ██║██║╚██╔╝██║       ██║   ██╔══██╗██╔══██║██║  ██║██╔══╝      ██║     ██╔══██║██╔═══╝    ██║   ██║   ██║██╔══██╗██╔══╝
// ██║     ██║  ██║╚██████╔╝██║ ╚═╝ ██║       ██║   ██║  ██║██║  ██║██████╔╝███████╗    ╚██████╗██║  ██║██║        ██║   ╚██████╔╝██║  ██║███████╗
// ╚═╝     ╚═╝  ╚═╝ ╚═════╝ ╚═╝     ╚═╝       ╚═╝   ╚═╝  ╚═╝╚═╝  ╚═╝╚═════╝ ╚══════╝     ╚═════╝╚═╝  ╚═╝╚═╝        ╚═╝    ╚═════╝ ╚═╝  ╚═╝╚══════╝

export const updateSwapFromTradeCapture = (
  currentSwap: FxSwap,
  sessionId: string,
  checkedSwapResponse: CheckedSwap,
): FxSwap => {
  const isNonDeliverable = checkedSwapResponse.changedFields.isNonDeliverable ?? currentSwap.isNonDeliverable;

  const properties = isNonDeliverable
    ? updateNdsSwapPropertiesFromTradeCapture(currentSwap, checkedSwapResponse)
    : updateRegularSwapPropertiesFromTradeCapture(currentSwap, checkedSwapResponse);

  // safeguard: nearExpiryDate/farExpiryDate can still be an unresolved tenor (e.g. "1W", "SP")
  // instead of an ISO date at this point, even when trade capture reports isReadyToPrice
  const areDatesResolved = isIsoDate(properties.nearExpiryDate ?? '') && isIsoDate(properties.farExpiryDate ?? '');

  const tradeCapture = {
    status: 'CHECKED',
    sessionId,
    version: checkedSwapResponse.idVersion,

    isReadyToPrice: (checkedSwapResponse.changedFields.isReadyToPrice ?? false) && areDatesResolved,
    isPriceObsolete: checkedSwapResponse.changedFields.isPriceObsolete ?? true,
    isProductObsolete: checkedSwapResponse.changedFields.isProductObsolete ?? true,

    errors: isDefined(checkedSwapResponse.errors) ? mapToCheckedSwapErrorsOrWarnings(checkedSwapResponse.errors) : {},
    warnings: isDefined(checkedSwapResponse.warnings)
      ? mapToCheckedSwapErrorsOrWarnings(checkedSwapResponse.warnings)
      : {},
  } satisfies TradeCapture<FxSwap['properties']>;

  return {
    isNonDeliverable,
    properties,
    tradeCapture,
    type: 'FxSwap',
    clientId: currentSwap.clientId,
  };
};

function mapToCheckedSwapErrorsOrWarnings<TInput extends TradeCaptureResponseErrors | TradeCaptureResponseWarnings>(
  inputs: TInput,
): TInput extends TradeCaptureResponseErrors
  ? TradeCaptureErrors<FxSwap['properties']>
  : TradeCaptureWarnings<FxSwap['properties']> {
  const leg = inputs.legs?.['0'];

  if (isNotDefined(leg)) {
    return {};
  }

  return {
    nearNotional: isDefined(leg.nearAmount1)
      ? leg.nearAmount1
      : isDefined(leg.nearAmount2)
        ? leg.nearAmount2
        : undefined,
    nearExpiryDate: leg.nearPaymentDate,

    farNotional: isDefined(leg.farAmount1) ? leg.farAmount1 : isDefined(leg.farAmount2) ? leg.farAmount2 : undefined,
    farExpiryDate: leg.farPaymentDate,
  };
}

const updateRegularSwapPropertiesFromTradeCapture = (
  currentSwap: FxSwap,
  checkedSwap: CheckedSwap,
): FxSwapRegular['properties'] => {
  const leg = checkedSwap.changedFields.legs['0'];
  assertIsDefined(leg, 'changedFields.legs["0"] is missing for a regular swap trade capture update');

  const type =
    (leg.isUneven === false && leg.swapOffMarket === false) ||
    (leg.isUneven === false && leg.swapOffMarket === undefined) ||
    (leg.isUneven === undefined && leg.swapOffMarket === false)
      ? 'standard'
      : leg.isUneven === true
        ? 'uneven'
        : leg.swapOffMarket === true
          ? 'historical'
          : !currentSwap.isNonDeliverable
            ? currentSwap.properties.type
            : 'standard';

  return {
    currencyPair: checkedSwap.changedFields.currencyPair ?? currentSwap.properties.currencyPair,
    currency1: checkedSwap.changedFields.currency1 ?? currentSwap.properties.currency1,
    currency2: checkedSwap.changedFields.currency2 ?? currentSwap.properties.currency2,

    type,

    // NEAR LEG

    nearNotional: getNotional(
      leg.nearAmount1,
      leg.nearAmount2,
      currentSwap.properties.nearNotional,
      getErrorPreviousNumberValue('nearAmount1', checkedSwap.errors) ??
        getErrorPreviousNumberValue('nearAmount2', checkedSwap.errors),
    ),

    nearNotionalCurrencyIndex: getNotionalCurrencyIndex(
      leg.nearAmount1,
      leg.nearAmount2,
      currentSwap.properties.nearNotionalCurrencyIndex,
    ),

    nearExpiryDate:
      getErrorPreviousStringDate('nearPaymentDate', checkedSwap.errors) ??
      // don't store tenor, only ISO date
      (leg.nearPaymentDate !== undefined
        ? leg.nearPaymentDate
        : isIsoDate(currentSwap.properties.nearExpiryDate ?? '')
          ? currentSwap.properties.nearExpiryDate
          : undefined),

    nearExpiryDateTenor:
      leg.nearPaymentDateTenor !== undefined ? leg.nearPaymentDateTenor : currentSwap.properties.nearExpiryDateTenor,

    // if trade capture returns a null value, or if the far price is defined, then near price is undefined
    // if trade capture returns undefined, then the current swap near price is used
    // else the trade capture near price is used
    nearPrice:
      leg.nearPriceReferenceBid === null ||
      leg.nearPriceReferenceAsk === null ||
      isDefined(leg.farPriceReferenceBid) ||
      isDefined(leg.farPriceReferenceAsk)
        ? undefined
        : leg.nearPriceReferenceBid === undefined && leg.nearPriceReferenceAsk === undefined
          ? currentSwap.properties.nearPrice
          : {
              bid: leg.nearPriceReferenceBid,
              ask: leg.nearPriceReferenceAsk,
            },

    // FAR LEG

    farNotional: getNotional(
      leg.farAmount1,
      leg.farAmount2,
      currentSwap.properties.farNotional,
      getErrorPreviousNumberValue('farAmount1', checkedSwap.errors) ??
        getErrorPreviousNumberValue('farAmount2', checkedSwap.errors),
    ),

    farNotionalCurrencyIndex: getNotionalCurrencyIndex(
      leg.farAmount1,
      leg.farAmount2,
      currentSwap.properties.farNotionalCurrencyIndex,
    ),

    farExpiryDate:
      getErrorPreviousStringDate('farPaymentDate', checkedSwap.errors) ??
      // don't store tenor, only ISO date
      (leg.farPaymentDate !== undefined
        ? leg.farPaymentDate
        : isIsoDate(currentSwap.properties.farExpiryDate ?? '')
          ? currentSwap.properties.farExpiryDate
          : undefined),

    farExpiryDateTenor:
      leg.farPaymentDateTenor !== undefined ? leg.farPaymentDateTenor : currentSwap.properties.farExpiryDateTenor,

    // if trade capture returns a null value, or if the near price is defined, then far price is undefined
    // if trade capture returns undefined, then the current swap far price is used
    // else the trade capture far price is used
    farPrice:
      leg.farPriceReferenceBid === null ||
      leg.farPriceReferenceAsk === null ||
      isDefined(leg.nearPriceReferenceBid) ||
      isDefined(leg.nearPriceReferenceAsk)
        ? undefined
        : leg.farPriceReferenceBid === undefined && leg.farPriceReferenceAsk === undefined
          ? !currentSwap.isNonDeliverable
            ? currentSwap.properties.farPrice
            : undefined
          : {
              bid: leg.farPriceReferenceBid,
              ask: leg.farPriceReferenceAsk,
            },

    // the following field must be kept to survice to unpinning of the near price
    commercialMarginSpotPoints: currentSwap.properties.commercialMarginSpotPoints,
    commercialMarginSwapPoints: currentSwap.properties.commercialMarginSwapPoints,
    commercialMarginAmountCurrencyIndex: currentSwap.properties.commercialMarginAmountCurrencyIndex,

    rfs: currentSwap.properties.rfs,
  } satisfies FxSwapRegular['properties'];
};

const updateNdsSwapPropertiesFromTradeCapture = (
  currentSwap: FxSwap,
  checkedSwap: CheckedSwap,
): FxSwapNds['properties'] => {
  const currentNdsSwap = currentSwap.isNonDeliverable
    ? currentSwap
    : {
        properties: {
          currencyPair: currentSwap.properties.currencyPair,
          currency1: currentSwap.properties.currency1,
          currency2: currentSwap.properties.currency2,

          nearNotional: currentSwap.properties.nearNotional,
          nearNotionalCurrencyIndex: currentSwap.properties.nearNotionalCurrencyIndex,
          nearExpiryDate: currentSwap.properties.nearExpiryDate,
          nearExpiryDateTenor: currentSwap.properties.nearExpiryDateTenor,
          nearPrice: currentSwap.properties.nearPrice,

          farNotional: currentSwap.properties.farNotional,
          farNotionalCurrencyIndex: currentSwap.properties.farNotionalCurrencyIndex,
          farExpiryDate: currentSwap.properties.farExpiryDate,
          farExpiryDateTenor: currentSwap.properties.farExpiryDateTenor,
          farPrice: currentSwap.properties.farPrice,

          // default values for NDS
          isCrossed: false,

          fixingCurrency: undefined,

          xCurrency: undefined,
          possibleXCurrencies: { default: undefined, possible: [] },

          fixingSource: undefined,
          possibleFixingSources: { default: undefined, possible: [] },

          sndFixingSource: undefined,
          possibleSndFixingSources: { default: undefined, possible: [] },

          nearFixingDate: undefined,
          farFixingDate: undefined,
        },
      };

  const leg = checkedSwap.changedFields.legs['0'];
  assertIsDefined(leg, 'changedFields.legs["0"] is missing for an NDS swap trade capture update');

  return {
    currencyPair: checkedSwap.changedFields.currencyPair ?? currentNdsSwap.properties.currencyPair,
    currency1: checkedSwap.changedFields.currency1 ?? currentNdsSwap.properties.currency1,
    currency2: checkedSwap.changedFields.currency2 ?? currentNdsSwap.properties.currency2,

    // NEAR LEG

    nearNotional: getNotional(
      leg.nearAmount1,
      leg.nearAmount2,
      currentSwap.properties.nearNotional,
      getErrorPreviousNumberValue('nearAmount1', checkedSwap.errors) ??
        getErrorPreviousNumberValue('nearAmount2', checkedSwap.errors),
    ),

    nearNotionalCurrencyIndex: getNotionalCurrencyIndex(
      leg.nearAmount1,
      leg.nearAmount2,
      currentSwap.properties.nearNotionalCurrencyIndex,
    ),

    nearExpiryDate:
      leg.nearPaymentDate ??
      // don't store tenor, only ISO date
      (isIsoDate(currentSwap.properties.nearExpiryDate ?? '') ? currentSwap.properties.nearExpiryDate : undefined),
    nearExpiryDateTenor: leg.nearPaymentDateTenor ?? currentSwap.properties.nearExpiryDateTenor,

    // if trade capture returns a null value, or if the far price is defined, then near price is undefined
    // if trade capture returns undefined, then the current swap near price is used
    // else the trade capture near price is used
    nearPrice:
      leg.nearPriceReferenceBid === null ||
      leg.nearPriceReferenceAsk === null ||
      isDefined(leg.farPriceReferenceBid) ||
      isDefined(leg.farPriceReferenceAsk)
        ? undefined
        : leg.nearPriceReferenceBid === undefined && leg.nearPriceReferenceAsk === undefined
          ? currentSwap.properties.nearPrice
          : {
              bid: leg.nearPriceReferenceBid,
              ask: leg.nearPriceReferenceAsk,
            },

    // FAR LEG

    farNotional: getNotional(
      leg.farAmount1,
      leg.farAmount2,
      currentSwap.properties.farNotional,
      getErrorPreviousNumberValue('farAmount1', checkedSwap.errors) ??
        getErrorPreviousNumberValue('farAmount2', checkedSwap.errors),
    ),

    farNotionalCurrencyIndex: getNotionalCurrencyIndex(
      leg.farAmount1,
      leg.farAmount2,
      currentSwap.properties.farNotionalCurrencyIndex,
    ),

    farExpiryDate:
      leg.farPaymentDate ??
      // don't store tenor, only ISO date
      (isIsoDate(currentSwap.properties.farExpiryDate ?? '') ? currentSwap.properties.farExpiryDate : undefined),
    farExpiryDateTenor: leg.farPaymentDateTenor ?? currentSwap.properties.farExpiryDateTenor,

    // the following field must be kept to survice to unpinning of the near price
    commercialMarginSpotPoints: currentSwap.properties.commercialMarginSpotPoints,
    commercialMarginSwapPoints: currentSwap.properties.commercialMarginSwapPoints,
    commercialMarginAmountCurrencyIndex: currentSwap.properties.commercialMarginAmountCurrencyIndex,

    isCrossed: leg.isCrossed ?? currentNdsSwap.properties.isCrossed,

    xCurrency: leg.xCurrency ?? currentNdsSwap.properties.xCurrency,
    possibleXCurrencies: leg.possibleXCurrencies ?? currentNdsSwap.properties.possibleXCurrencies,

    fixingCurrency: leg.fixingCurrency ?? currentNdsSwap.properties.fixingCurrency,

    fixingSource: leg.fixingSource ?? currentNdsSwap.properties.fixingSource,
    possibleFixingSources: leg.possibleFixingSources ?? currentNdsSwap.properties.possibleFixingSources,

    sndFixingSource: leg.sndFixingSource ?? currentNdsSwap.properties.sndFixingSource,
    possibleSndFixingSources: leg.possibleSndFixingSources ?? currentNdsSwap.properties.possibleSndFixingSources,

    nearFixingDate: leg.nearFixingDate ?? currentNdsSwap.properties.nearFixingDate,
    farFixingDate: leg.farFixingDate ?? currentNdsSwap.properties.farFixingDate,

    // RFS
    rfs: currentSwap.properties.rfs,
  } satisfies FxSwapNds['properties'];
};
